import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';

export default function AngelScene() {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || sceneRef.current) return;
    sceneRef.current = true;

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#000000');
    scene.fog = new THREE.Fog('#000000', 8, 25);

    const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 100);
    camera.position.z = 8;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    container.appendChild(renderer.domElement);

    // Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.15);
    scene.add(ambientLight);
    const dirLight1 = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight1.position.set(5, 5, 5);
    scene.add(dirLight1);
    const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
    dirLight2.position.set(-3, -2, -5);
    scene.add(dirLight2);
    const pointLight = new THREE.PointLight(0xffffff, 0.6, 20);
    pointLight.position.set(0, 3, 3);
    scene.add(pointLight);

    // Central torusknot (angel core)
    const torusKnotGeo = new THREE.TorusKnotGeometry(1.2, 0.35, 200, 32, 2, 3);
    const torusKnotMat = new THREE.MeshStandardMaterial({ color: 0xffffff, wireframe: true, metalness: 0.8, roughness: 0.2 });
    const torusKnot = new THREE.Mesh(torusKnotGeo, torusKnotMat);
    const coreGroup = new THREE.Group();
    coreGroup.add(torusKnot);

    // Wings (ExtrudeGeometry)
    const createWing = (scaleX) => {
      const shape = new THREE.Shape();
      shape.moveTo(0, 0);
      shape.bezierCurveTo(0.5, 2, 1.5, 3.5, 0.3, 5);
      shape.bezierCurveTo(1.8, 4, 3, 3.5, 4, 4.5);
      shape.bezierCurveTo(3.5, 3, 2.5, 1.5, 0, 0);
      const geo = new THREE.ExtrudeGeometry(shape, { depth: 0.05, bevelEnabled: true, bevelThickness: 0.02, bevelSize: 0.02, bevelSegments: 3 });
      const mat = new THREE.MeshStandardMaterial({ color: 0xffffff, metalness: 0.95, roughness: 0.05 });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.scale.set(0.35 * scaleX, 0.35, 0.35);
      mesh.position.set(scaleX > 0 ? 1.8 : -1.8, -0.5, 0);
      return mesh;
    };
    const wingL = createWing(1);
    const wingR = createWing(-1);
    coreGroup.add(wingL);
    coreGroup.add(wingR);
    scene.add(coreGroup);

    // Outer glow sphere
    const glowGeo = new THREE.SphereGeometry(2.5, 32, 32);
    const glowMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.015 });
    const glowMesh = new THREE.Mesh(glowGeo, glowMat);
    coreGroup.add(glowMesh);

    // Floating particles
    const particleCount = 80;
    const particleGroup = new THREE.Group();
    for (let i = 0; i < particleCount; i++) {
      const geo = new THREE.SphereGeometry(0.015, 6, 6);
      const mat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.25 + Math.random() * 0.2 });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(
        (Math.random() - 0.5) * 20,
        (Math.random() - 0.5) * 15,
        (Math.random() - 0.5) * 20
      );
      particleGroup.add(mesh);
    }
    scene.add(particleGroup);

    // Halo ring
    const haloGeo = new THREE.TorusGeometry(2, 0.01, 16, 100);
    const haloMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.15 });
    const halo = new THREE.Mesh(haloGeo, haloMat);
    halo.position.y = 2.5;
    halo.rotation.x = Math.PI * 0.4;
    coreGroup.add(halo);

    // Mouse
    let mouseX = 0, mouseY = 0;
    const onMouseMove = (e) => {
      mouseX = (e.clientX / width - 0.5) * 2;
      mouseY = (e.clientY / height - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMouseMove);

    // Animate
    let animId;
    const clock = new THREE.Clock();
    const animate = () => {
      animId = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();

      coreGroup.rotation.y = t * 0.15 + mouseX * 0.3;
      coreGroup.position.y = Math.sin(t * 0.5) * 0.3;
      torusKnot.rotation.x = t * 0.3;
      torusKnot.rotation.z = t * 0.2;

      wingL.rotation.y = Math.sin(t * 0.4) * 0.3;
      wingR.rotation.y = -Math.sin(t * 0.4) * 0.3;

      particleGroup.rotation.y = t * 0.02;
      halo.rotation.z = t * 0.1;

      camera.position.y = -mouseY * 0.3;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
    };
    animate();

    // Resize
    const onResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(animId);
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      sceneRef.current = false;
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
      data-testid="angel-3d-scene"
    />
  );
}
